import React, { useEffect, useMemo, useState } from "react";
import { Star, Plus, Search, X as XIcon, ChevronRight, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import Gated from "@/components/Gated";
import AddLeadModal from "@/components/AddLeadModal";
import LeadDrawer from "@/components/LeadDrawer";
import OnboardingWizard from "@/components/OnboardingWizard";
import { api } from "@/lib/api";
import { STAGES, TERMINAL_STAGES, CATEGORIES, CATEGORY_COLORS, CATEGORY_LEFT_RULE, CATEGORY_RANK, STAGE_COLORS, fmtINR, fmtDate } from "@/lib/leadUtils";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useIsMobile } from "@/hooks/useIsMobile";

const FILTER_DEFAULTS = { category: "", stage: "", platform: "", organic: "", bucket: "", starred: false, going_cold: false, campaign: "" };
const DENSITY_KEY = "ls.pipeline.density";  // localStorage key
const DENSITY_OPTS = [
  { v: "spacious", l: "Spacious" },
  { v: "auto", l: "Auto" },
  { v: "compact", l: "Compact" },
];
const COMPACT_THRESHOLD = 6;

function effectiveCategory(l) {
  return l.category || l.suggested_category || null;
}

function priorityRank(l) {
  return [
    l.starred ? 0 : 1,
    CATEGORY_RANK[effectiveCategory(l)] ?? 99,
  ];
}

function sortInColumn(items) {
  return [...items].sort((a, b) => {
    const ra = priorityRank(a), rb = priorityRank(b);
    if (ra[0] !== rb[0]) return ra[0] - rb[0];
    if (ra[1] !== rb[1]) return ra[1] - rb[1];
    // tertiary: next_followup_at asc if both set; else created_in_app_at desc
    const aFu = a.next_followup_at, bFu = b.next_followup_at;
    if (aFu && bFu) return aFu.localeCompare(bFu);
    if (aFu && !bFu) return -1;
    if (!aFu && bFu) return 1;
    return (b.created_in_app_at || "").localeCompare(a.created_in_app_at || "");
  });
}

export default function Pipeline() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(() => ({
    ...FILTER_DEFAULTS,
    campaign: searchParams.get("campaign") || "",
    platform: searchParams.get("platform") || "",
  }));
  const [addOpen, setAddOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [collapsed, setCollapsed] = useState({ DNP: true, Rejected: true });
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem(DENSITY_KEY) || "auto"; } catch { return "auto"; }
  });

  // Phase 7.1 Pass 2A — mobile-only state.
  // `mobileStage` drives which single stage's leads render below the chip row.
  // Defaults to "Followup" (the most-common active stage). Filter-sheet open
  // state is separate from `filters` so we don't dirty the existing wiring.
  const [mobileStage, setMobileStage] = useState("Followup");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Phase 4.1 wizard state
  const [wizardCtx, setWizardCtx] = useState(null);  // { lead, existingProject, eventTypes, serviceTypes }
  const [reonboardConfirm, setReonboardConfirm] = useState(null);  // { lead }
  const [catalogs, setCatalogs] = useState({ event_types: [], service_types: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/catalogs");
        setCatalogs(data);
      } catch {}
    })();
  }, []);

  const openWizardFor = async (lead) => {
    // Look up active + previous-cancelled in parallel — determines which flow to show.
    let activeProject = null;
    let previousCancelled = null;
    try {
      if (lead.project_id) {
        // Active project exists on the lead's response
        const { data: pj } = await api.get(`/projects/${lead.project_id}`);
        activeProject = pj.project;
      }
    } catch {}
    if (!activeProject) {
      try {
        const { data } = await api.get(`/leads/${lead.lead_id}/previous-project`);
        previousCancelled = data.project;
      } catch {}
    }
    if (activeProject) {
      setReonboardConfirm({ lead, mode: "edit", existingProject: activeProject });
    } else if (previousCancelled) {
      setReonboardConfirm({ lead, mode: "after-cancel", existingProject: previousCancelled });
    } else {
      setWizardCtx({
        lead, existingProject: null,
        eventTypes: (catalogs.event_types || []).filter((e) => e.active).map((e) => e.name),
        serviceTypes: (catalogs.service_types || []).filter((s) => s.active).map((s) => s.name),
      });
    }
  };

  const continueReonboard = async (useExisting) => {
    const { lead, mode, existingProject } = reonboardConfirm;
    setReonboardConfirm(null);
    setWizardCtx({
      lead,
      existingProject: useExisting ? existingProject : null,
      // For "after-cancel" + Use previous: we want to pre-fill but NOT carry over advance_payment.
      // The wizard already excludes advance_payment from existingProject.advance_payment prefill
      // by overriding amount when the prop is null. We strip advance_payment from the passed object.
      ...(mode === "after-cancel" && useExisting ? {
        existingProject: { ...existingProject, advance_payment: null, status: undefined },
      } : {}),
      eventTypes: (catalogs.event_types || []).filter((e) => e.active).map((e) => e.name),
      serviceTypes: (catalogs.service_types || []).filter((s) => s.active).map((s) => s.name),
    });
  };

  const confirmReonboard = async () => continueReonboard(true);

  // sync URL → filters when user navigates via Dashboard click
  useEffect(() => {
    const c = searchParams.get("campaign") || "";
    const p = searchParams.get("platform") || "";
    setFilters((f) => ({ ...f, campaign: c, platform: p }));
  }, [searchParams]);

  useEffect(() => {
    try { localStorage.setItem(DENSITY_KEY, density); } catch {}
  }, [density]);

  const fetchLeads = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (filters.category) params.category = filters.category;
    if (filters.stage) params.stage = filters.stage;
    if (filters.platform) params.platform = filters.platform;
    if (filters.campaign) params.campaign = filters.campaign;
    if (filters.organic) params.organic = filters.organic;
    if (filters.starred) params.starred = true;
    if (filters.going_cold) params.min_unanswered = 3;
    const { data } = await api.get("/leads", { params });
    setLeads(data.items || []);
  };

  useEffect(() => { fetchLeads(); /* eslint-disable-next-line */ }, [q, filters.category, filters.stage, filters.platform, filters.organic, filters.starred, filters.going_cold, filters.campaign]);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s, []]));
    for (const l of leads) (m[l.stage] || m.Followup).push(l);
    for (const s of STAGES) m[s] = sortInColumn(m[s]);
    return m;
  }, [leads]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    if (!over) return;
    const targetStage = over.id;
    const lead = leads.find((l) => l.lead_id === active.id);
    if (!lead || lead.stage === targetStage) return;
    // Phase 4.2 — Onboarded is one-way; reject drag-out with informational toast.
    if (lead.stage === "Onboarded" && targetStage !== "Onboarded") {
      toast("Onboarded leads cannot be moved back. Use Cancel Project on the project.", { duration: 5000 });
      return;
    }
    // Phase 4.1 — intercept Onboarded transitions, open wizard instead of patching
    if (targetStage === "Onboarded") { openWizardFor(lead); return; }
    try {
      const { data } = await api.patch(`/leads/${lead.lead_id}`, { stage: targetStage });
      setLeads((cur) => cur.map((x) => x.lead_id === data.lead_id ? data : x));
      toast.success(`Moved to ${targetStage}`);
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(det || "Move failed");
    }
  };

  // Auto-density: compact if any non-terminal column has >6 cards
  const autoCompact = useMemo(() => STAGES.some((s) => !TERMINAL_STAGES.has(s) && (byStage[s]?.length || 0) > COMPACT_THRESHOLD), [byStage]);
  const useCompact = density === "compact" || (density === "auto" && autoCompact);

  // Phase 7.1 Pass 2A — count active filters for the mobile Filters button
  // badge. Search (`q`) is intentionally excluded because it has its own
  // visible input on mobile.
  const activeFilterCount = [
    filters.category, filters.stage, filters.platform, filters.organic,
    filters.campaign,
  ].filter(Boolean).length + (filters.starred ? 1 : 0) + (filters.going_cold ? 1 : 0);

  return (
    <Layout>
      <div className="fade-up" data-testid="pipeline-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <div className="kicker mb-2 lg:mb-3" style={{ color: "var(--ds-accent)" }}>PIPELINE</div>
            <h1
              data-testid="pipeline-title"
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: "clamp(30px, 8.5vw, 62px)",
                color: "var(--ds-ink)",
                letterSpacing: "-0.01em",
                lineHeight: 1,
                fontWeight: 400,
              }}
            >
              Leads
            </h1>
            <div className="text-sm mt-2" style={{ color: "var(--ds-text-2)" }} data-testid="leads-count">{leads.length} leads</div>
          </div>
          {/* Desktop toolbar — density + view toggles + Add-lead. Hidden on
              mobile (Pass 2A spec §2): mobile drops these entirely; the FAB
              replaces Add-lead and there is no toggle. */}
          {!isMobile && (
          <div className="flex items-center gap-3 flex-wrap">
            {/* Density segmented toggle */}
            <div
              data-testid="density-switcher"
              className="flex"
              style={{
                background: "#FFF",
                border: "1px solid #E9E2D6",
                borderRadius: 8,
                padding: 4,
              }}
            >
              {DENSITY_OPTS.map((o) => (
                <button
                  key={o.v}
                  onClick={() => setDensity(o.v)}
                  data-testid={`density-${o.v}`}
                  style={{
                    padding: "9px 18px",
                    borderRadius: 5,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    background: density === o.v ? "var(--ds-ink)" : "transparent",
                    color: density === o.v ? "#F4EFE4" : "#857E6F",
                    transition: "all .18s ease",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {/* View segmented toggle */}
            <div
              data-testid="view-switcher"
              className="flex"
              style={{
                background: "#FFF",
                border: "1px solid #E9E2D6",
                borderRadius: 8,
                padding: 4,
              }}
            >
              {["kanban", "table"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  data-testid={`view-${v}`}
                  style={{
                    padding: "9px 18px",
                    borderRadius: 5,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    background: view === v ? "var(--ds-ink)" : "transparent",
                    color: view === v ? "#F4EFE4" : "#857E6F",
                    transition: "all .18s ease",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
            <Gated resource="leads_write">
            <button
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2"
              style={{
                background: "var(--ds-ink)",
                color: "#F4EFE4",
                padding: "11px 18px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 6px 14px rgba(27,38,31,.18)",
                transition: "background-color .18s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-ink-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--ds-ink)")}
              data-testid="add-lead-button"
            >
              <Plus size={14} strokeWidth={2.2} /> Add lead
            </button>
            </Gated>
          </div>
          )}
        </div>

        {/* Filter bar — Phase 7.0 Screen D: white pill surface.
            Mobile (Pass 2A §2): compact search + Filters button that opens
            a bottom sheet. Desktop keeps the inline filter bar unchanged. */}
        {isMobile ? (
          <div className="flex items-center gap-2 mb-4" data-testid="filter-bar">
            <div className="relative flex-1 min-w-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-muted)" }} />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, phone, email…"
                className="w-full outline-none"
                style={{
                  background: "#FCFAF5",
                  border: "1px solid #E4DCCD",
                  borderRadius: 12,
                  padding: "11px 12px 11px 34px",
                  fontSize: 13.5,
                  color: "var(--ds-text)",
                  fontFamily: "var(--ds-body-font)",
                }}
                data-testid="search-input"
              />
            </div>
            <button
              onClick={() => setFilterSheetOpen(true)}
              data-testid="mobile-filter-button"
              className="inline-flex items-center gap-1.5 shrink-0"
              style={{
                background: activeFilterCount > 0 ? "var(--ds-ink)" : "#FFFDFA",
                color: activeFilterCount > 0 ? "#F4EFE4" : "var(--ds-ink)",
                border: activeFilterCount > 0 ? "1px solid var(--ds-ink)" : "1px solid #E4DCCD",
                padding: "11px 14px",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "var(--ds-accent)",
                    color: "#F4EFE4",
                    borderRadius: 999,
                    padding: "1px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                    marginLeft: 2,
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        ) : (
        <div
          className="flex flex-wrap items-center gap-2 mb-6"
          data-testid="filter-bar"
          style={{
            background: "#FFF",
            border: "1px solid #EDE5D8",
            borderRadius: 10,
            padding: "12px 14px",
            boxShadow: "0 2px 6px rgba(60,46,20,.05)",
          }}
        >
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-muted)" }} />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, email…"
              className="w-full pl-9 pr-3 outline-none"
              style={{
                background: "#FCFAF5",
                border: "1px solid #E4DCCD",
                borderRadius: 8,
                padding: "9px 12px 9px 34px",
                fontSize: 13,
                color: "var(--ds-text)",
                fontFamily: "var(--ds-body-font)",
                transition: "border-color .15s, box-shadow .15s",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#B08D55";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(176,141,85,.12)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#E4DCCD";
                e.currentTarget.style.boxShadow = "none";
              }}
              data-testid="search-input"
            />
          </div>
          {filters.campaign && (
            <span
              data-testid="active-campaign-chip"
              className="inline-flex items-center gap-2"
              style={{
                background: "var(--ds-ink)",
                color: "#F4EFE4",
                borderRadius: 9999,
                padding: "7px 13px",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Campaign: {filters.campaign}
              <button
                onClick={() => {
                  setFilters((f) => ({ ...f, campaign: "", platform: "" }));
                  setSearchParams({}, { replace: true });
                }}
                className="hover:opacity-70"
                data-testid="clear-campaign"
                title="Clear campaign + platform filter"
              ><XIcon size={12} /></button>
            </span>
          )}
          <FilterSelect testId="filter-category" value={filters.category} onChange={(v) => setFilters((f) => ({ ...f, category: v }))} options={[["", "All categories"], ...CATEGORIES.map((c) => [c, c])]} />
          <FilterSelect testId="filter-stage" value={filters.stage} onChange={(v) => setFilters((f) => ({ ...f, stage: v }))} options={[["", "All stages"], ...STAGES.map((s) => [s, s])]} />
          <FilterSelect testId="filter-platform" value={filters.platform} onChange={(v) => { setFilters((f) => ({ ...f, platform: v })); const sp = new URLSearchParams(searchParams); if (v) sp.set("platform", v); else sp.delete("platform"); setSearchParams(sp, { replace: true }); }} options={[["", "All platforms"], ...["Instagram", "Facebook", "Manual", "WedMeGood", "Other"].map((s) => [s, s])]} />
          <FilterSelect testId="filter-organic" value={filters.organic} onChange={(v) => setFilters((f) => ({ ...f, organic: v }))} options={[["", "Paid + Organic"], ["organic", "Organic"], ["paid", "Paid"]]} />
          <Pill active={filters.starred} onClick={() => setFilters((f) => ({ ...f, starred: !f.starred }))} testId="filter-starred">★ Starred only</Pill>
          <Pill active={filters.going_cold} onClick={() => setFilters((f) => ({ ...f, going_cold: !f.going_cold }))} testId="filter-going-cold" color="var(--ds-neg)" bg="var(--ds-neg-bg)">Going cold (3+)</Pill>
        </div>
        )}

        {/* Mobile stage-chip selector (Pass 2A §3) — horizontally scrollable
            row showing all live pipeline stages with their live counts. One
            selected at a time; drives the visible stage list below. Reuses
            the existing `byStage` grouping (built above from live data). */}
        {isMobile && (
          <div
            data-testid="mobile-stage-chips"
            style={{
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              paddingBottom: 6,
              marginBottom: 12,
              marginLeft: -4,
              marginRight: -4,
              paddingLeft: 4,
              paddingRight: 4,
            }}
          >
            <style>{`[data-testid="mobile-stage-chips"]::-webkit-scrollbar { display: none; }`}</style>
            {STAGES.map((stage) => {
              const count = (byStage[stage] || []).length;
              const selected = mobileStage === stage;
              return (
                <button
                  key={stage}
                  onClick={() => setMobileStage(stage)}
                  data-testid={`mobile-stage-chip-${stage}`}
                  style={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "9px 14px",
                    borderRadius: 999,
                    background: selected ? "var(--ds-ink)" : "#FFFDFA",
                    color: selected ? "#F4EFE4" : "var(--ds-ink)",
                    border: selected ? "1px solid var(--ds-ink)" : "1px solid #E4DCCD",
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    boxShadow: selected ? "0 4px 12px rgba(34,56,44,0.18)" : "none",
                    transition: "background .18s ease, box-shadow .18s ease",
                  }}
                >
                  <span>{stage}</span>
                  <span
                    style={{
                      background: selected ? "var(--ds-accent)" : "#EFE8DC",
                      color: selected ? "#F4EFE4" : "var(--ds-text-2)",
                      borderRadius: 999,
                      padding: "2px 8px",
                      fontSize: 11,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      minWidth: 22,
                      textAlign: "center",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Mobile section label — selected stage + count (spec §4). */}
        {isMobile && (
          <div className="flex items-baseline gap-2 mb-4" data-testid="mobile-stage-label">
            <span
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: 20,
                color: STAGE_COLORS[mobileStage] || "var(--ds-ink)",
                fontWeight: 500,
              }}
            >
              {mobileStage}
            </span>
            <span style={{ fontSize: 13, color: "var(--ds-text-2)" }}>
              · {(byStage[mobileStage] || []).length} leads
            </span>
          </div>
        )}

        {isMobile ? (
          <MobileLeadList
            leads={byStage[mobileStage] || []}
            onCardClick={setDrawerId}
            reload={fetchLeads}
          />
        ) : view === "kanban" ? (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className="flex gap-3 overflow-x-auto pb-6" data-testid="kanban-board" style={{ maxHeight: "calc(100vh - 280px)" }}>
              {STAGES.map((stage) => {
                const isTerminal = TERMINAL_STAGES.has(stage);
                const isCol = isTerminal && collapsed[stage];
                return (
                  <KanbanColumn key={stage} stage={stage} items={byStage[stage] || []} collapsed={isCol} compact={useCompact} onToggle={() => setCollapsed((c) => ({ ...c, [stage]: !c[stage] }))} onCardClick={setDrawerId} reload={fetchLeads} />
                );
              })}
            </div>
          </DndContext>
        ) : (
          <LeadsTable leads={leads} onRowClick={setDrawerId} reload={fetchLeads} openWizardFor={openWizardFor} />
        )}

        {/* Mobile Add-lead FAB (Pass 2A §7). Isolated stacking context so it
            never falls behind the platform Emergent badge or the tab bar. */}
        {isMobile && (
          <Gated resource="leads_write">
            <div
              style={{
                position: "fixed",
                right: 20,
                bottom: `calc(env(safe-area-inset-bottom, 0px) + 92px)`,
                zIndex: 10001,
                isolation: "isolate",
              }}
              data-testid="mobile-add-lead-fab-wrap"
            >
              <button
                onClick={() => setAddOpen(true)}
                data-testid="mobile-add-lead-fab"
                aria-label="Add lead"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "var(--ds-ink)",
                  color: "#F4EFE4",
                  border: "none",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  boxShadow: "0 8px 20px rgba(27,38,31,0.32), 0 2px 6px rgba(27,38,31,0.18)",
                  transition: "transform .15s ease, box-shadow .15s ease",
                }}
                onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.94)")}
                onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <Plus size={26} strokeWidth={2.3} />
              </button>
            </div>
          </Gated>
        )}

        {/* Mobile Filters bottom sheet (Pass 2A §4). */}
        {isMobile && (
          <MobileFilterSheet
            open={filterSheetOpen}
            onClose={() => setFilterSheetOpen(false)}
            filters={filters}
            setFilters={setFilters}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}

        <AddLeadModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={(payload) => { fetchLeads(); const id = payload?.lead_id || payload; if (id) setDrawerId(id); }} />
        <LeadDrawer leadId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} onChange={fetchLeads} onStageOnboardAttempt={(lead) => openWizardFor(lead)} />

        {reonboardConfirm && (
          <ReonboardConfirm
            lead={reonboardConfirm.lead}
            mode={reonboardConfirm.mode}
            onUsePrevious={() => continueReonboard(true)}
            onStartFresh={() => continueReonboard(false)}
            onCancel={() => setReonboardConfirm(null)}
          />
        )}
        {wizardCtx && (
          <OnboardingWizard
            lead={wizardCtx.lead}
            existingProject={wizardCtx.existingProject}
            eventTypes={wizardCtx.eventTypes}
            serviceTypes={wizardCtx.serviceTypes}
            onClose={() => setWizardCtx(null)}
            onComplete={() => { setWizardCtx(null); fetchLeads(); }}
          />
        )}
      </div>
    </Layout>
  );
}

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{
        background: "#FCFAF5",
        border: "1px solid #E4DCCD",
        borderRadius: 8,
        padding: "9px 12px",
        fontSize: 13,
        color: "var(--ds-text)",
        fontFamily: "var(--ds-body-font)",
        cursor: "pointer",
      }}
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Pill({ active, onClick, children, color = "var(--ds-accent)", bg = "var(--ds-accent-bg)", testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: "9px 17px",
        border: `1px solid ${active ? color : "#E4DCCD"}`,
        background: active ? bg : "#FFF",
        borderRadius: 9999,
        fontSize: 13,
        fontWeight: 500,
        color: active ? color : "#4A4439",
        cursor: "pointer",
        transition: "border-color .15s, color .15s",
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.borderColor = "var(--ds-accent)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.borderColor = "#E4DCCD";
      }}
    >
      {children}
    </button>
  );
}

function KanbanColumn({ stage, items, collapsed, compact, onToggle, onCardClick, reload }) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  if (collapsed) {
    return (
      <button ref={setNodeRef} onClick={onToggle} className="shrink-0 border border-brand-border flex flex-col items-center justify-start py-3" style={{ width: 42, background: isOver ? "var(--brand-paper)" : "#fff" }} data-testid={`column-${stage}-collapsed`}>
        <div className="text-[10px] tracking-[0.2em] uppercase" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", color: STAGE_COLORS[stage] }}>
          {stage} ({items.length})
        </div>
      </button>
    );
  }
  return (
    <div ref={setNodeRef} className="shrink-0 w-[280px] flex flex-col" data-testid={`column-${stage}`} style={{ background: isOver ? "var(--brand-paper)" : "transparent", maxHeight: "100%" }}>
      <div
        className="flex items-center justify-between mb-3 sticky top-0 z-10 py-2"
        data-testid={`column-${stage}-header`}
        style={{ background: isOver ? "var(--ds-accent-bg)" : "var(--ds-canvas)" }}
      >
        <div className="flex items-center gap-2">
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#A39A8B",
            }}
          >
            {stage}
          </span>
          <span
            style={{
              background: "#EFE8DC",
              borderRadius: 5,
              padding: "3px 8px",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--ds-text-2)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {items.length}
          </span>
        </div>
        {TERMINAL_STAGES.has(stage) && (
          <button
            onClick={onToggle}
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--ds-text-2)",
            }}
          >
            collapse
          </button>
        )}
      </div>
      {/* Phase 7.0 R-Pipeline-R1 — scrollable card-list contained in a warm
          surface box. Same treatment for every column (even empty/short) so
          the kanban reads as a row of cohesive boxes rather than free-floating
          stacks. Bottom mask fades the last ~30px of scroll content so cards
          soften out rather than getting clipped at the column's lower edge. */}
      <div
        style={{
          background: "#FCFAF5",
          border: "1px solid #EDE5D8",
          borderRadius: 14,
          padding: 10,
          boxShadow: "0 1px 3px rgba(60,46,20,.04)",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="space-y-2 overflow-y-auto"
          style={{
            flex: 1,
            paddingRight: 4,
            WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 30px), transparent 100%)",
            maskImage: "linear-gradient(to bottom, black calc(100% - 30px), transparent 100%)",
          }}
        >
          {items.map((l) =>
            compact
              ? <CompactCard key={l.lead_id} lead={l} onClick={() => onCardClick(l.lead_id)} reload={reload} />
              : <LeadCard key={l.lead_id} lead={l} onClick={() => onCardClick(l.lead_id)} reload={reload} />
          )}
          {items.length === 0 && <div className="text-xs italic px-2 py-6 text-center" style={{ color: "var(--ds-muted)" }}>—</div>}
        </div>
      </div>
    </div>
  );
}

function _leftRule(lead) {
  // Solid 3px when applied; dashed 3px (same color) when only suggested.
  if (lead.category && CATEGORY_LEFT_RULE[lead.category]) {
    return `3px solid ${CATEGORY_LEFT_RULE[lead.category]}`;
  }
  if (lead.suggested_category && CATEGORY_LEFT_RULE[lead.suggested_category]) {
    return `3px dashed ${CATEGORY_LEFT_RULE[lead.suggested_category]}`;
  }
  return "1px solid var(--brand-border)";
}

function LeadCard({ lead, onClick, reload }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.lead_id });
  // Phase 7.0 Screen D — floating-pill style. Ace cards get a 3px gold
  // accent strip across the top (kept from the legacy treatment, recolored).
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    background: "#FFF",
    border: "1px solid #EDE5D8",
    borderRadius: 12,
    padding: "16px 18px",
    boxShadow: "0 2px 8px rgba(60,46,20,.06)",
    borderTop: lead.category === "Ace" ? "3px solid var(--ds-accent)" : "1px solid #EDE5D8",
    transition: "box-shadow .15s ease, transform .15s ease",
    cursor: "pointer",
    position: "relative",
  };
  const toggleStar = async (e) => { e.stopPropagation(); await api.patch(`/leads/${lead.lead_id}`, { starred: !lead.starred }); reload(); };
  const applySuggested = async (e) => { e.stopPropagation(); await api.patch(`/leads/${lead.lead_id}`, { category: lead.suggested_category }); reload(); };
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      onClick={onClick}
      data-testid={`lead-card-${lead.lead_id}`}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 16px rgba(60,46,20,.12)";
        e.currentTarget.style.transform = (transform ? `translate3d(${transform.x}px, ${transform.y}px, 0) ` : "") + "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(60,46,20,.06)";
        e.currentTarget.style.transform = transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : "";
      }}
    >
      <button onClick={toggleStar} className="absolute top-3 right-3" data-testid={`card-star-${lead.lead_id}`}>
        <Star size={14} fill={lead.starred ? "#C8A063" : "transparent"} color={lead.starred ? "#C8A063" : "#CFC4AE"} strokeWidth={2} />
      </button>
      {lead.followup_bucket === "overdue" && (
        <span
          className="absolute"
          style={{ top: 10, right: 28, width: 7, height: 7, borderRadius: 9999, background: "var(--ds-neg)" }}
          data-testid="overdue-dot"
        />
      )}
      <div
        data-testid={`card-name-${lead.lead_id}`}
        style={{
          fontFamily: "var(--ds-num-font)",
          fontSize: 17,
          color: "var(--ds-ink)",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
          paddingRight: 38,
        }}
      >
        {lead.full_name || "—"}
      </div>
      {lead.is_sample && (
        <span
          className="inline-block mt-1.5"
          style={{
            background: "var(--ds-canvas)",
            color: "var(--ds-text-2)",
            border: "1px dashed #D8CCBB",
            borderRadius: 9999,
            fontSize: 9,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            padding: "2px 8px",
            fontWeight: 600,
          }}
          title="Sample — delete me when ready."
          data-testid={`card-sample-pill-${lead.lead_id}`}
        >
          Sample
        </span>
      )}
      <div className="flex items-center gap-2 mt-2.5">
        <InlineSelect value={lead.category || ""} options={[["", "—"], ...CATEGORIES.map((c) => [c, c])]} onChange={async (v) => { await api.patch(`/leads/${lead.lead_id}`, { category: v || null }); reload(); }} colors={CATEGORY_COLORS[lead.category]} testId={`card-category-${lead.lead_id}`} />
        {lead.followup_count > 0 && (
          <span style={{ fontSize: 10, color: "var(--ds-text-2)", fontWeight: 600 }}>FU {lead.followup_count}</span>
        )}
      </div>
      {!lead.category && lead.suggested_category && (
        <button onClick={applySuggested} className="mt-2 inline-flex items-center" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", padding: "3px 9px", border: `1px dashed ${CATEGORY_LEFT_RULE[lead.suggested_category]}`, color: CATEGORY_LEFT_RULE[lead.suggested_category], borderRadius: 9999, fontWeight: 600 }} data-testid={`card-apply-suggested-${lead.lead_id}`}>
          ✨ {lead.suggested_category}
        </button>
      )}
      <div className="flex gap-1.5 flex-wrap mt-2.5">
        {lead.platform && (
          <span style={{ fontSize: 10, padding: "3px 8px", background: "#F1EDE2", color: "#A39A8B", borderRadius: 4, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{lead.platform}</span>
        )}
        <span style={{ fontSize: 10, padding: "3px 8px", background: "#F1EDE2", color: "#A39A8B", borderRadius: 4, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{lead.is_organic ? "Organic" : "Paid"}</span>
      </div>
      {lead.summary && <div style={{ fontSize: 12, color: "var(--ds-text-2)", marginTop: 10 }} className="line-clamp-2">{lead.summary}</div>}
      {lead.quote_value && (
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            marginTop: 10,
            color: "var(--ds-accent)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtINR(lead.quote_value)}
        </div>
      )}
    </div>
  );
}

function CompactCard({ lead, onClick, reload }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.lead_id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    borderLeft: _leftRule(lead),
    borderTop: "1px solid var(--brand-border)",
    borderRight: "1px solid var(--brand-border)",
    borderBottom: "1px solid var(--brand-border)",
  };
  const toggleStar = async (e) => { e.stopPropagation(); await api.patch(`/leads/${lead.lead_id}`, { starred: !lead.starred }); reload(); };
  const tooltip = [lead.summary, lead.platform && `${lead.platform} · ${lead.is_organic ? "Organic" : "Paid"}`].filter(Boolean).join(" — ");
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className="bg-white px-2.5 py-1.5 cursor-pointer hover:shadow-sm relative flex items-center gap-2" style={style} onClick={onClick} title={tooltip} data-testid={`lead-card-compact-${lead.lead_id}`}>
      <button onClick={toggleStar} data-testid={`card-star-${lead.lead_id}`}>
        <Star size={11} fill={lead.starred ? "var(--brand-champagne)" : "transparent"} color={lead.starred ? "var(--brand-champagne)" : "var(--brand-muted)"} />
      </button>
      <span className="text-[12px] font-medium truncate flex-1" data-testid={`card-name-${lead.lead_id}`}>{lead.full_name || "—"}</span>
      {lead.is_sample && (
        <span
          className="text-[8px] tracking-[0.16em] uppercase px-1 py-0.5 shrink-0"
          style={{
            background: "var(--brand-paper-soft)",
            color: "var(--brand-muted)",
            border: "1px dashed var(--brand-border-soft)",
            borderRadius: 9999,
          }}
          title="Sample — delete me when ready."
          data-testid={`card-sample-pill-${lead.lead_id}`}
        >
          Sample
        </span>
      )}
      {lead.category && <span className="text-[9px] tracking-[0.14em] uppercase px-1.5 py-0.5 shrink-0" style={{ background: CATEGORY_COLORS[lead.category]?.bg, color: CATEGORY_COLORS[lead.category]?.fg, borderRadius: 9999 }} data-testid={`card-category-${lead.lead_id}`}>{lead.category}</span>}
      {lead.followup_count > 0 && <span className="text-[9px] text-brand-muted shrink-0">FU {lead.followup_count}</span>}
      {lead.quote_value && <span className="text-[11px] font-medium shrink-0" style={{ color: "var(--brand-success)" }}>{fmtINR(lead.quote_value)}</span>}
      {lead.followup_bucket === "overdue" && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--brand-danger)" }} data-testid="overdue-dot" />}
    </div>
  );
}

function InlineSelect({ value, options, onChange, colors, testId, optionDisabled, disabledTooltip }) {
  // Phase 7.0 Screen D — category/stage chip dimensions per spec:
  // font-weight 700, 11px, letter-spacing 0.1em, padding 6/12, radius 7.
  // Reused by LeadCard (kanban, in scope) and by LeadsTable (out of scope
  // for this pass — only the chip widget itself adopts the new sizing;
  // table layout is unchanged).
  const fg = colors?.fg || "var(--ds-text-2)";
  const bg = colors?.bg || "transparent";
  const border = colors?.border || "#E4DCCD";
  const cur = options.find(([v]) => v === value);
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()} title={disabledTooltip || ""}>
      <span
        style={{
          display: "inline-block",
          background: bg,
          color: fg,
          border: `1px solid ${border}`,
          borderRadius: 7,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.1em",
          padding: "6px 12px",
          textTransform: "uppercase",
        }}
        data-testid={testId}
      >
        {cur?.[1] || "—"}
      </span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer">
        {options.map(([v, l]) => {
          const isDisabled = optionDisabled ? optionDisabled(v) : false;
          return <option key={v} value={v} disabled={isDisabled}>{l}{isDisabled ? " (disabled)" : ""}</option>;
        })}
      </select>
    </span>
  );
}

function LeadsTable({ leads, onRowClick, reload, openWizardFor }) {
  return (
    <div className="bg-white border border-brand-border overflow-x-auto" data-testid="leads-table">
      <table className="w-full text-sm">
        <thead className="bg-brand-paper text-brand-muted text-[11px] tracking-[0.18em] uppercase">
          <tr>
            <th className="p-3 text-left">★</th>
            <th className="p-3 text-left">Name</th>
            <th className="p-3 text-left">Category</th>
            <th className="p-3 text-left">Stage</th>
            <th className="p-3 text-right">Quote</th>
            <th className="p-3 text-left">Platform</th>
            <th className="p-3 text-left">Organic/Paid</th>
            <th className="p-3 text-left">Campaign</th>
            <th className="p-3 text-left">Created</th>
            <th className="p-3 text-left">Next FU</th>
            <th className="p-3 text-left">Phone</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.lead_id} onClick={() => onRowClick(l.lead_id)} className="border-t border-brand-border hover:bg-brand-paper cursor-pointer" data-testid={`row-${l.lead_id}`}>
              <td className="p-3" onClick={(e) => e.stopPropagation()}>
                <button onClick={async () => { await api.patch(`/leads/${l.lead_id}`, { starred: !l.starred }); reload(); }}>
                  <Star size={14} fill={l.starred ? "var(--brand-champagne)" : "transparent"} color={l.starred ? "var(--brand-champagne)" : "var(--brand-muted)"} />
                </button>
              </td>
              <td className="p-3 font-medium">{l.full_name || "—"}</td>
              <td className="p-3"><InlineSelect value={l.category || ""} options={[["", "—"], ...CATEGORIES.map((c) => [c, c])]} onChange={async (v) => { await api.patch(`/leads/${l.lead_id}`, { category: v || null }); reload(); }} colors={CATEGORY_COLORS[l.category]} testId={`row-${l.lead_id}-category`} /></td>
              <td className="p-3"><InlineSelect value={l.stage} options={STAGES.map((s) => [s, s])}
                optionDisabled={(v) => l.stage === "Onboarded" && v !== "Onboarded"}
                disabledTooltip={l.stage === "Onboarded" ? "Use Cancel Project to revert this lead." : ""}
                onChange={async (v) => {
                if (v === "Onboarded") { openWizardFor(l); return; }
                try { await api.patch(`/leads/${l.lead_id}`, { stage: v }); reload(); }
                catch (err) {
                  const det = err?.response?.data?.detail;
                  toast.error(det || "Update failed");
                }
              }} colors={{ bg: STAGE_COLORS[l.stage], fg: "#fff" }} testId={`row-${l.lead_id}-stage`} /></td>
              <td className="p-3 text-right">{fmtINR(l.quote_value)}</td>
              <td className="p-3">{l.platform || "—"}</td>
              <td className="p-3">{l.is_organic ? "Organic" : "Paid"}</td>
              <td className="p-3">{l.campaign_name || "—"}</td>
              <td className="p-3 text-brand-muted">{fmtDate(l.created_in_app_at)}</td>
              <td className="p-3 text-brand-muted">{fmtDate(l.next_followup_at)}</td>
              <td className="p-3 text-brand-muted">{l.phone_display || "—"}</td>
            </tr>
          ))}
          {leads.length === 0 && (
            <tr><td colSpan="11" className="p-8 text-center text-brand-muted italic">No leads match these filters.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReonboardConfirm({ lead, mode, onUsePrevious, onStartFresh, onCancel }) {
  const isAfterCancel = mode === "after-cancel";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.45)" }} onClick={onCancel} data-testid="reonboard-confirm-modal">
      <div className="bg-white w-full max-w-[460px] p-7" onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-2">{isAfterCancel ? "RE-ONBOARD" : "EDIT PROJECT"}</div>
        <h2 className="font-display text-2xl mb-2" style={{ color: "var(--brand-ink)" }} data-testid="reonboard-title">
          {isAfterCancel
            ? `${lead?.full_name || "This lead"} had a cancelled project.`
            : `${lead?.full_name || "This lead"} already has a project.`}
        </h2>
        <p className="text-sm text-brand-muted mb-6">
          {isAfterCancel ? "Re-onboard with previous details pre-filled?" : "Open the wizard to edit existing details?"}
        </p>
        <div className="flex justify-end gap-3 flex-wrap">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-brand-border" data-testid="reonboard-cancel">Cancel</button>
          {isAfterCancel && (
            <button onClick={onStartFresh} className="px-4 py-2 text-sm border border-brand-border" data-testid="reonboard-start-fresh">Start fresh</button>
          )}
          <button onClick={onUsePrevious} className="px-5 py-2 text-sm font-medium text-white" style={{ background: "var(--brand-primary)" }} data-testid="reonboard-yes">
            {isAfterCancel ? "Use previous details" : "Yes, edit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 7.1 Pass 2A — Mobile-only Pipeline components.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mobile list of leads for a single stage. Each card is tappable (opens the
 * existing lead drawer) and mirrors the desktop LeadCard's field surface:
 * star toggle, name (Newsreader), category + platform/organic chips + FU
 * counter, ₹ value (right side), chevron affordance, and a colored left
 * accent edge (category rule — reuses the same `_leftRule` logic conceptually).
 * Renders an empty-state placeholder when the stage has 0 leads.
 */
function MobileLeadList({ leads, onCardClick, reload }) {
  if (!leads || leads.length === 0) {
    return (
      <div
        data-testid="mobile-lead-list-empty"
        style={{
          textAlign: "center",
          padding: "48px 20px 40px",
          color: "var(--ds-text-2)",
        }}
      >
        <div style={{ fontSize: 34, color: "#D8CCBB", marginBottom: 8, lineHeight: 1 }}>—</div>
        <div style={{ fontSize: 13, fontStyle: "italic" }}>No leads in this stage</div>
      </div>
    );
  }
  return (
    <div
      data-testid="mobile-lead-list"
      style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}
    >
      {leads.map((l) => (
        <MobileLeadCard key={l.lead_id} lead={l} onClick={() => onCardClick(l.lead_id)} reload={reload} />
      ))}
    </div>
  );
}

/**
 * Compact mobile lead card. Layout:
 *   [★] [name + chips row]                 [₹ value] [›]
 *   left accent rule reflects category (solid = applied, dashed = suggested).
 */
function MobileLeadCard({ lead, onClick, reload }) {
  const toggleStar = async (e) => {
    e.stopPropagation();
    try {
      await api.patch(`/leads/${lead.lead_id}`, { starred: !lead.starred });
      reload();
    } catch {
      toast.error("Star failed");
    }
  };
  // Left accent rule — solid if category is applied, dashed if only suggested.
  const cat = lead.category;
  const sug = lead.suggested_category;
  const catColor =
    (cat && CATEGORY_LEFT_RULE[cat]) ||
    (sug && CATEGORY_LEFT_RULE[sug]) ||
    "transparent";
  const catDashed = !cat && !!sug;
  return (
    <div
      onClick={onClick}
      data-testid={`lead-card-${lead.lead_id}`}
      style={{
        background: "#FFFDFA",
        borderRadius: 16,
        boxShadow: "0 5px 16px rgba(34,56,44,0.055), 0 1px 2px rgba(34,56,44,0.04)",
        padding: "14px 14px 14px 18px",
        position: "relative",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 12,
        overflow: "hidden",
      }}
    >
      {/* Left accent rule */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0, top: 8, bottom: 8,
          width: 4,
          borderRadius: 3,
          background: catColor,
          borderLeft: catDashed ? `4px dashed ${catColor}` : "none",
          opacity: catColor === "transparent" ? 0 : 1,
        }}
      />
      {/* Star */}
      <button
        onClick={toggleStar}
        data-testid={`card-star-${lead.lead_id}`}
        aria-label={lead.starred ? "Unstar" : "Star"}
        style={{
          flexShrink: 0,
          padding: 4,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Star
          size={17}
          fill={lead.starred ? "#C8A063" : "transparent"}
          color={lead.starred ? "#C8A063" : "#CFC4AE"}
          strokeWidth={2}
        />
      </button>
      {/* Name + chips column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          data-testid={`card-name-${lead.lead_id}`}
          style={{
            fontFamily: "var(--ds-num-font)",
            fontSize: 16,
            color: "var(--ds-ink)",
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {lead.full_name || "—"}
        </div>
        <div className="flex gap-1.5 items-center" style={{ marginTop: 6, flexWrap: "wrap" }}>
          {lead.category && (
            <span
              data-testid={`card-category-${lead.lead_id}`}
              style={{
                background: CATEGORY_COLORS[lead.category]?.bg,
                color: CATEGORY_COLORS[lead.category]?.fg,
                border: `1px solid ${CATEGORY_COLORS[lead.category]?.border || "transparent"}`,
                borderRadius: 6,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.1em",
                padding: "2px 7px",
                textTransform: "uppercase",
              }}
            >
              {lead.category}
            </span>
          )}
          {!lead.category && lead.suggested_category && (
            <span
              style={{
                border: `1px dashed ${CATEGORY_LEFT_RULE[lead.suggested_category]}`,
                color: CATEGORY_LEFT_RULE[lead.suggested_category],
                borderRadius: 6,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.1em",
                padding: "2px 7px",
                textTransform: "uppercase",
              }}
            >
              {lead.suggested_category}
            </span>
          )}
          {lead.followup_count > 0 && (
            <span
              style={{
                background: "#F1EDE2",
                color: "#A39A8B",
                borderRadius: 6,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                padding: "2px 7px",
                textTransform: "uppercase",
              }}
            >
              FU{lead.followup_count}
            </span>
          )}
          {lead.followup_bucket === "overdue" && (
            <span
              data-testid="overdue-dot"
              aria-label="Overdue follow-up"
              style={{
                width: 7, height: 7,
                borderRadius: 9999,
                background: "var(--ds-neg)",
                marginLeft: 2,
              }}
            />
          )}
        </div>
      </div>
      {/* Value + chevron column */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
          minWidth: 0,
        }}
      >
        {lead.quote_value ? (
          <span
            style={{
              fontFamily: "var(--ds-num-font)",
              fontSize: 15,
              fontWeight: 500,
              color: "var(--ds-accent)",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {fmtINR(lead.quote_value)}
          </span>
        ) : null}
        <ChevronRight size={16} color="rgba(34,56,44,0.32)" />
      </div>
    </div>
  );
}

/**
 * Mobile Filters bottom sheet — slides up from the bottom, dark scrim behind,
 * dismisses on scrim tap / Clear / Apply. Renders the SAME live filter
 * controls the desktop uses (category, stage, platform, organic, starred,
 * going_cold), wired to the same `filters` state — no new logic.
 */
function MobileFilterSheet({ open, onClose, filters, setFilters, searchParams, setSearchParams }) {
  const [draft, setDraft] = useState(filters);

  // Reset the draft when the sheet opens so any prior unapplied edits
  // don't leak into the next open.
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  if (!open) return null;

  const apply = () => {
    setFilters(draft);
    // Keep the URL in sync with platform/campaign (desktop parity).
    const sp = new URLSearchParams(searchParams);
    if (draft.platform) sp.set("platform", draft.platform); else sp.delete("platform");
    if (draft.campaign) sp.set("campaign", draft.campaign); else sp.delete("campaign");
    setSearchParams(sp, { replace: true });
    onClose();
  };

  const clear = () => {
    setDraft(FILTER_DEFAULTS);
    setFilters(FILTER_DEFAULTS);
    setSearchParams({}, { replace: true });
    onClose();
  };

  return (
    <>
      <div
        data-testid="mobile-filter-scrim"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 10050,
          background: "rgba(20,30,24,0.42)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="mobile-filter-sheet"
        style={{
          position: "fixed",
          left: 0, right: 0, bottom: 0,
          zIndex: 10051,
          background: "#FFFDFA",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: "0 -12px 40px rgba(20,30,24,0.28)",
          padding: `20px 20px calc(env(safe-area-inset-bottom, 0px) + 20px)`,
          maxHeight: "82vh",
          overflowY: "auto",
          isolation: "isolate",
        }}
      >
        {/* Drag handle */}
        <div
          aria-hidden="true"
          style={{
            width: 44, height: 5,
            background: "rgba(34,56,44,0.18)",
            borderRadius: 999,
            margin: "0 auto 14px",
          }}
        />
        <div className="flex items-center justify-between mb-4">
          <span style={{ fontFamily: "var(--ds-num-font)", fontSize: 20, color: "var(--ds-ink)" }}>
            Filters
          </span>
          <button
            onClick={clear}
            data-testid="mobile-filter-clear"
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--ds-accent)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 6,
            }}
          >
            Clear
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SheetSelect
            testId="filter-category"
            value={draft.category}
            onChange={(v) => setDraft((d) => ({ ...d, category: v }))}
            options={[["", "All categories"], ...CATEGORIES.map((c) => [c, c])]}
          />
          <SheetSelect
            testId="filter-stage"
            value={draft.stage}
            onChange={(v) => setDraft((d) => ({ ...d, stage: v }))}
            options={[["", "All stages"], ...STAGES.map((s) => [s, s])]}
          />
          <SheetSelect
            testId="filter-platform"
            value={draft.platform}
            onChange={(v) => setDraft((d) => ({ ...d, platform: v }))}
            options={[["", "All platforms"], ...["Instagram", "Facebook", "Manual", "WedMeGood", "Other"].map((s) => [s, s])]}
          />
          <SheetSelect
            testId="filter-organic"
            value={draft.organic}
            onChange={(v) => setDraft((d) => ({ ...d, organic: v }))}
            options={[["", "Paid + Organic"], ["organic", "Organic"], ["paid", "Paid"]]}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
          <SheetToggle
            testId="filter-starred"
            label="★ Starred only"
            active={draft.starred}
            onToggle={() => setDraft((d) => ({ ...d, starred: !d.starred }))}
          />
          <SheetToggle
            testId="filter-going-cold"
            label="Going cold (3+)"
            active={draft.going_cold}
            onToggle={() => setDraft((d) => ({ ...d, going_cold: !d.going_cold }))}
            color="var(--ds-neg)"
            bg="var(--ds-neg-bg)"
          />
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            onClick={onClose}
            data-testid="mobile-filter-cancel"
            style={{
              flex: 1,
              padding: "13px 16px",
              border: "1px solid #E4DCCD",
              background: "#FFFDFA",
              color: "var(--ds-ink)",
              borderRadius: 12,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={apply}
            data-testid="mobile-filter-apply"
            style={{
              flex: 1,
              padding: "13px 16px",
              background: "var(--ds-ink)",
              color: "#F4EFE4",
              border: "none",
              borderRadius: 12,
              fontSize: 13.5,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 6px 14px rgba(27,38,31,0.24)",
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

function SheetSelect({ value, onChange, options, testId }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 14px",
        background: "#FCFAF5",
        border: "1px solid #E4DCCD",
        borderRadius: 12,
      }}
    >
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        style={{
          flex: 1,
          background: "transparent",
          border: "none",
          outline: "none",
          fontSize: 14,
          color: "var(--ds-text)",
          fontFamily: "var(--ds-body-font)",
          cursor: "pointer",
          appearance: "none",
          WebkitAppearance: "none",
        }}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
      <span
        aria-hidden="true"
        style={{
          color: "var(--ds-muted)",
          fontSize: 11,
          pointerEvents: "none",
          marginLeft: 8,
        }}
      >
        ▾
      </span>
    </div>
  );
}

function SheetToggle({ label, active, onToggle, color = "var(--ds-accent)", bg = "var(--ds-accent-bg)", testId }) {
  return (
    <button
      onClick={onToggle}
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "13px 14px",
        border: `1px solid ${active ? color : "#E4DCCD"}`,
        background: active ? bg : "#FFFDFA",
        color: active ? color : "var(--ds-text)",
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        style={{
          width: 22, height: 22,
          borderRadius: 999,
          border: `1.5px solid ${active ? color : "#CFC4AE"}`,
          background: active ? color : "transparent",
          display: "grid",
          placeItems: "center",
          color: "#F4EFE4",
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {active ? "✓" : ""}
      </span>
    </button>
  );
}

